import React, { useMemo } from "react";
import { splitZipParts, joinZipParts } from "../patientForm.shared";

const labelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";
const inputClass =
  "block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100";
const inputErrorClass =
  "border-red-400 focus:border-red-500 focus:ring-red-100";

/**
 * Two-box ZIP UI (XXXXX + XXXX) bound to a single `value` string (e.g. "91324-2023").
 */
const ZipCodeInput = ({
  label = "Zip Code",
  value = "",
  onChange,
  error,
  required = false,
  disabled = false,
  className = "",
}) => {
  const { base, plus4 } = useMemo(() => splitZipParts(value), [value]);

  const emit = (nextBase, nextPlus4) => {
    onChange?.(joinZipParts(nextBase, nextPlus4));
  };

  return (
    <div className={`space-y-1 min-w-0 ${className}`}>
      <label className={labelClass}>
        {label}
        {required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </label>
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          disabled={disabled}
          value={base}
          maxLength={5}
          placeholder="XXXXX"
          aria-label={`${label} first 5 digits`}
          className={`${inputClass} w-[5.5rem] tabular-nums ${error ? inputErrorClass : ""}`}
          onChange={(e) => {
            const digits = e.target.value.replaceAll(/\D/g, "").slice(0, 5);
            emit(digits, plus4);
          }}
        />
        <span className="text-slate-400 font-medium select-none" aria-hidden>
          -
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="postal-code-suffix"
          disabled={disabled}
          value={plus4}
          maxLength={4}
          placeholder="XXXX"
          aria-label={`${label} last 4 digits`}
          className={`${inputClass} w-[4.25rem] tabular-nums ${error ? inputErrorClass : ""}`}
          onChange={(e) => {
            const digits = e.target.value.replaceAll(/\D/g, "").slice(0, 4);
            emit(base, digits);
          }}
        />
      </div>
      {error ? (
        <p className="text-[11px] text-red-600 mt-0.5">{error}</p>
      ) : null}
    </div>
  );
};

export default ZipCodeInput;
